/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Importing required libraries and modules
import { useDispatch, useSelector } from "react-redux"; // Redux hooks for dispatching actions and selecting state
import { useTranslation } from "react-i18next"; // Hook for handling internationalization and translations
import { useLocation, useNavigate } from "react-router-dom"; // React Router hooks for navigation and accessing location state
import { getAllContractors } from "../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsSlice"; // Selector to get contractor list from Redux store
import { useEffect, useState } from "react"; // React hooks for state and side effects
import { Contract } from "../../../../../models/Contract"; // Contract model used to initialize the contract state
import { GetAllContractors } from "../../../../../reduxToolkit/EmployeeContractors/EmployeeContractorsApi";
import routes from '../../../../../Routes/routes';
import {TABS_CONTRACTORS} from '../../Model/Models'; // API call to fetch all contractors

// Component responsible for managing common contract creation and update logic
const CommonContractApp = () => {
    const { t } = useTranslation(); // Translation function for localized text
    let dispatch = useDispatch(); // Redux dispatch function
    let navigate = useNavigate(); // Hook to navigate programmatically
    const location = useLocation(); // Hook to access the current route's state

    // Extracting necessary pieces of state from Redux
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Development flag from common state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Permissions from authentication state
    const getAllContractor = useSelector(getAllContractors); // List of all contractors from Redux store

    // Local state to manage the contract data and validations
    const [contract, setContract] = useState(Contract); // Contract data initialized from model
    const [emptyFields, setEmptyFields] = useState(false); // Flag to detect empty required fields
    const [isValidStartDate, setIsValidStartDate] = useState(true); // Validity check for start date
    const [isValidEndDate, setIsValidEndDate] = useState(true); // Validity check for end date
    const [modified, setModified] = useState(false);
    const state = location.state; // Access route state passed through navigation (if any)
    const contractId = state && state.state; // Extract contract ID from the route state (used for editing contracts)

    // Function to update contract state when the start date is selected
    const handleStartDateChange = (selectedDate) => {
        const startDateInMilliseconds = selectedDate.getTime(); // Convert selected date to timestamp
        setContract((prevData) => ({
            ...prevData,
            startDate: startDateInMilliseconds, // Set start date in contract state
        }));
    };

    // Function to update contract state when the end date is selected
    const handleEndDateChange = (selectedDate) => {
        const endDateInMilliseconds = selectedDate.getTime(); // Convert selected date to timestamp
        setContract((prevData) => ({
            ...prevData,
            endDate: endDateInMilliseconds, // Set end date in contract state
        }));
    };

    // Function to clear the contract form
    const handleClearAll = () => {
        setContract(Contract); // Reset contract to initial model values
    };

    // Effect to fetch all contractors when the component loads or contract ID changes
    useEffect(() => {
        dispatch(GetAllContractors()); // Dispatch action to get all contractors from the backend
    }, [contractId]); // This will re-run when contractId changes

    const handleOnBack = () => {
        navigate(routes.contractor.list, {
            state: {currentTab:TABS_CONTRACTORS.CONTRACTS.INDEX}
        });
    }

    // Returning the necessary data and functions to be used in the component’s UI or logic
    return {
        t,
        DEV,
        permission,
        dispatch,
        navigate,
        handleClearAll,
        contractId,
        contract,
        setContract,
        emptyFields,
        setEmptyFields,
        isValidStartDate,
        setIsValidStartDate,
        isValidEndDate,
        setIsValidEndDate,
        getAllContractor,
        modified,
        setModified,
        handleStartDateChange,
        handleEndDateChange,
        handleOnBack
    };
}

export default CommonContractApp; // Exporting the component for use in other parts of the application
